import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Mail,
  Lock,
  Visibility,
  VisibilityOff,
  WavingHand,
  CheckCircleOutline,
  Person,
  Badge,
  Assignment,
} from '@mui/icons-material';
import { useSpring, animated, config } from 'react-spring';
import AuthService from './auth.service.js';
import AlertService from '../../components/Alerts/alerts.js';
import ConfigurationService from '../Configuration/configuration.service.js';
import backgroundImage from '../../assets/backgroundImage.png';
import DashboardService from "../Dashboard/dashboard.service.js";
import Alerts from "../../components/Alerts/alerts.js";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

export default function Register() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [theme, setTheme] = useState(createTheme());

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [Branch, setBranch] = useState('');
  const [mmcNumber, setMmcNumber] = useState('');
  const [apcNumber, setApcNumber] = useState('');

  // Success dialog state
  const [openDialog, setOpenDialog] = useState(false);
  
  // Configuration states for MMC/APC requirements
  const [mmcRequired, setMmcRequired] = useState(false);
  const [apcRequired, setApcRequired] = useState(false);

  const isMobile = useMediaQuery('(max-width:600px)');

  const fadeIn = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    config: config.molasses,
  });

  const slideUp = useSpring({
    transform: 'translateY(0px)',
    from: { transform: 'translateY(50px)' },
    config: config.wobbly,
  });

  useEffect(() => {
    getSetLanguage();
    getSystemConfig();
    fetchBusiness()
  }, []);

  function fetchBusiness() {
    setLoading(true);
    DashboardService.getAllBusiness().then((response) => {
      setLoading(false);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      }
      else {
        if (response.data.data) {
          //iph todo: 47
          //console.log("ALL Business", response.data.data);
          setBranch(response.data.data[0]);
        }
      }
    },
      (error) => {
        setLoading(false);
        const errContent = (error.response && error.response.data) || error.message || error.toString();
        //iph todo: 47
        //console.log("Net:-", errContent)
        if (errContent) {
          Alerts.swalErrorAlert(errContent)
        }
        else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."))
        }
      })
  };

  const getSetLanguage = () => {
    const language = localStorage.getItem('language') || 'en';
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
  };

  const getSystemConfig = async () => {
    try {
      const response = await ConfigurationService.getConfiguration();
      if (response.data.status) {
        const config = response.data.data[0];
        setTheme(createTheme({
          palette: {
            primary: {
              main: config.buttons_color,
            },
            secondary: {
              main: config.topbar_color,
            },
          },
        }));
        
        // Set MMC and APC requirements from configuration
        setMmcRequired(config.mmc_required || false);
        setApcRequired(config.apc_required || false);
      }
    } catch (error) {
      console.error('Error fetching configuration:', error);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneWithPlus = phone.startsWith('+') ? phone : `+${phone}`; // Ensure it starts with '+'
    const phoneRegex = /^\+[1-9]{1}[0-9]{3,14}$/; // Validates international phone number format
    return phoneRegex.test(phoneWithPlus);
  };

  const validateMmcNumber = (mmcNumber) => {
    const mmcRegex = /^[0-9]{4,6}$/; // Must be 4 to 6 digits
    return mmcRegex.test(mmcNumber);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !phone || !password || password !== confirmPassword) {
      AlertService.swalErrorAlert(t('Please fill in all fields correctly'));
      return;
    }

    if (!validateEmail(email)) {
      AlertService.swalErrorAlert(t('Please enter a valid email address'));
      return;
    }

    const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`;
    if (!validatePhone(formattedPhone)) {
      //iph todo: 47
      //console.log("phone", formattedPhone);
      AlertService.swalErrorAlert(t('Please enter a valid phone number with country code'));
      return;
    }

    // Validate MMC Number if required
    if (mmcRequired && !mmcNumber) {
      AlertService.swalErrorAlert(t('MMC Number is required'));
      return;
    }
    if (mmcNumber && !validateMmcNumber(mmcNumber)) {
      AlertService.swalErrorAlert(t('MMC Number must be 4 to 6 digits'));
      return;
    }

    // Validate APC Number if required
    if (apcRequired && !apcNumber) {
      AlertService.swalErrorAlert(t('APC Number is required'));
      return;
    }
    setLoading(true);
    let payload = {
      functionName: 'Register_User',
      postData: {
        name: name,
        email: email,
        business_id: Branch._id,
        password: password,
        phone: formattedPhone,
        status: 'Active',
        user_type: 'customers',
        mmc_number: mmcNumber || '',
        apc_number: apcNumber || '',
      }
    };

    try {
      const response = await AuthService.registerCustomer(payload);
      setLoading(false);
      if (response.data.status) {
        setOpenDialog(true);
      } else if (response.data.message === 'Email Already Registered') {
        AlertService.swalErrorAlert("Email Already Registered");
      } else {
        AlertService.swalErrorAlert(response.data.message);
      }
    } catch (error) {
      setLoading(false);
      AlertService.swalErrorAlert(t(error));
      const resMessage = error.response?.data?.message || error.message || "An error occurred";
      AlertService.swalErrorAlert(t(resMessage));
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `linear-gradient(to left, rgba(255,255,255,0.3), rgba(255,255,255,0.1)), url(${backgroundImage})`,
          backgroundSize: 'cover',
          p: { xs: 2, sm: 3 },
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <animated.div style={fadeIn}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                backgroundColor: '#fff',
                borderRadius: { xs: '12px', sm: '20px' },
                overflow: 'hidden',
                boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
              }}
            >
              {/* Left Side - Register Form */}
              <Box
                sx={{
                  flex: 1,
                  p: { xs: 3, sm: 4, md: 6 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: 2, sm: 3 }
                }}
              >
                <animated.div style={slideUp}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: theme.palette.primary.main,
                      mb: { xs: 1, sm: 2, md: 3 },
                      fontWeight: 500,
                      textAlign: { xs: 'center', sm: 'left' }
                    }}
                  >
                    Create Account <WavingHand color="primary" />
                  </Typography>
                </animated.div>

                {/* Register Form */}
                <Box
                  component="form"
                  onSubmit={onSubmit}
                  sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: 400 }
                  }}
                >
                  {/* Form Fields */}
                  <TextField
                    fullWidth
                    placeholder="Customer Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{
                      mb: { xs: 1.5, sm: 2 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        borderRadius: '50px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      mb: { xs: 1.5, sm: 2 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        borderRadius: '50px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Mail sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* <TextField
                    fullWidth
                    placeholder="Phone"
                    type='number'
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    sx={{
                      mb: { xs: 1.5, sm: 2 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        borderRadius: '50px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  /> */}

                  <PhoneInput
                    country={'my'}
                    value={phone}
                    onChange={(value) => setPhone(value)}
                    inputStyle={{
                      width: '100%',
                      height: '56px',
                      borderRadius: '50px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #ced4da',
                      paddingLeft: '48px',
                    }}
                    buttonStyle={{
                      borderRadius: '50px 0 0 50px',
                      backgroundColor: '#f8f9fa',
                    }}
                    dropdownStyle={{
                      borderRadius: '8px',
                      maxHeight: '250px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}
                    inputProps={{
                      name: 'phone',
                      required: true,
                    }}
                  />

                  {/* MMC Number Field */}
                  <TextField
                    fullWidth
                    placeholder={`MMC Number${mmcRequired ? ' *' : ' (Optional)'}`}
                    value={mmcNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                      if (value.length <= 6) { // Max 6 digits
                        setMmcNumber(value);
                      }
                    }}
                    sx={{
                      mb: { xs: 1.5, sm: 2 },
                      mt: { xs: 1.5, sm: 2 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        borderRadius: '50px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Badge sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                    helperText="4-6 digits required"
                  />

                  {/* APC Number Field */}
                  <TextField
                    fullWidth
                    placeholder={`APC Number${apcRequired ? ' *' : ' (Optional)'}`}
                    value={apcNumber}
                    onChange={(e) => setApcNumber(e.target.value)}
                    sx={{
                      mb: { xs: 1.5, sm: 2 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        borderRadius: '50px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Assignment sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />



                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{
                      mb: { xs: 1.5, sm: 2 },
                      mt: { xs: 1.5, sm: 2 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        borderRadius: '50px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    sx={{
                      mb: { xs: 1.5, sm: 2 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        borderRadius: '50px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{
                      py: { xs: 1, sm: 1.5 },
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '50px',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    {loading ? 'Signing up...' : 'SIGN UP'}
                  </Button>
                </Box>
              </Box>

              {/* Right Side - Login CTA */}
              <Box
                sx={{
                  flex: 1,
                  bgcolor: theme.palette.primary.main,
                  p: { xs: 4, sm: 6 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: { xs: '300px', md: 'auto' },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: { xs: '200px', sm: '300px' },
                    height: { xs: '200px', sm: '300px' },
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                    transform: 'translate(50%, -50%) rotate(45deg)',
                    borderRadius: '50px',
                  }
                }}
              >
                <animated.div style={slideUp}>
                  <Typography
                    variant="h3"
                    sx={{
                      mb: { xs: 2, sm: 3 },
                      fontWeight: 500,
                      fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                      textAlign: 'center'
                    }}
                  >
                    Welcome Back!
                  </Typography>
                </animated.div>
                <animated.div style={slideUp}>
                  <Typography
                    textAlign="center"
                    sx={{
                      mb: { xs: 3, sm: 4 },
                      maxWidth: 400,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      px: { xs: 2, sm: 0 }
                    }}
                  >
                    Already have an account? Sign in to access your dashboard and manage your services.
                  </Typography>
                </animated.div>
                <animated.div style={useSpring({
                  from: { opacity: 0, transform: 'scale(0.5)' },
                  to: { opacity: 1, transform: 'scale(1)' },
                  config: config.wobbly,
                })}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/customer_login')}
                    sx={{
                      color: 'white',
                      borderColor: 'white',
                      borderRadius: '50px',
                      px: { xs: 4, sm: 6 },
                      py: { xs: 1, sm: 1.5 },
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    SIGN IN
                  </Button>
                </animated.div>
              </Box>
            </Box>
          </animated.div>
        </Container>
      </Box>

      {/* Success Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: '20px',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            backgroundImage: `linear-gradient(to left, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3)), url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CheckCircleOutline sx={{ fontSize: '3rem', color: '#fff' }} />
          {"Registration Successful"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: 'white', fontSize: '1.2rem' }}
          >
            Your account registration is currently being verified.You will receive an email notification once the verification is complete.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            onClick={() => navigate('/customer_login')}
            autoFocus
            variant="contained"
            sx={{
              background: 'white',
              color: theme.palette.primary.main,
              fontWeight: 'bold',
              px: 4,
              py: 1,
              borderRadius: '30px',
              '&:hover': {
                background: '#f0f0f0',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}